import { useMemo } from "react";
import { useSelector } from "react-redux";
import { NavLink, useSearchParams } from "react-router";

import Avatar from "@/design/components/Avatar";
import { CodeBlock, highlighter } from "@/design/components/CodeBlock";
import HeaderCard from "@/design/components/HeaderCard";
import FormGroup from "@/design/forms/FormGroup";
import StringForm from "@/design/forms/StringForm";
import { type StoreState } from "@/store";
import Valkyrie, { viArrowRight, viWebhooks } from "@sippy-platform/valkyrie";

import { Button, CardHeader, Dialog, Grid, Stack, Typography } from "@mui/material";
import { createHighlightedCodeBlockProps } from "@tanstack/highlight/react";
import { format } from "date-fns";

import DataCell from "../_Components/DataCell";
import TableRow from "../_Components/TableRow";

export default function WebhookItem({
  webhookEvent,
}: {
  webhookEvent: {
    id: number;
    date: Date;
    name: string;
    did?: string;
    eventType: string;
    responseStatusCode?: string;
    responseData?: string;
    webhookId: string;
    url: string;
  };
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentSearchId = searchParams.get("id");
  const webhookEventId = String(webhookEvent.id);

  const linkSearchParams = useMemo(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("id", webhookEventId);

    return nextSearchParams.toString();
  }, [searchParams, webhookEventId]);

  const handleClose = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("id");
    setSearchParams(nextSearchParams);
  };

  const isDetailOpen = currentSearchId === webhookEventId;

  const pbx = useSelector((state: StoreState) => state.domain.pbx);

  return (
    <>
      <TableRow component={NavLink} to={`?${linkSearchParams}`} sx={{ "&:hover": { cursor: "pointer" } }}>
        <DataCell
          invertOrder
          noWrap
          tabular
          primary={format(webhookEvent.date, "pp")}
          secondary={format(webhookEvent.date, "PPP")}
        />
        <DataCell
          startDecorator={<Avatar text={webhookEvent.name} px={24} icon={viWebhooks} />}
          primary={webhookEvent.name}
        />
        <DataCell primary={webhookEvent.did} />
        <DataCell primary={webhookEvent.eventType} />
        <DataCell
          primary={webhookEvent.responseStatusCode ?? <i style={{ opacity: "0.6" }}>None</i>}
          secondary={webhookEvent.responseData}
        />
      </TableRow>
      <Dialog
        open={isDetailOpen}
        onClose={handleClose}
        maxWidth="md"
        slotProps={{ paper: { id: "eventDetailsDialog" } }}
      >
        <HeaderCard>
          <CardHeader
            avatar={<Avatar text={webhookEvent.name} icon={viWebhooks} />}
            title={webhookEvent.name}
            subheader={format(webhookEvent.date, "d MMMM yyyy, HH:mm:ss")}
            action={
              <Button
                color="inherit"
                endIcon={<Valkyrie icon={viArrowRight} />}
                component={NavLink}
                to={`/pbx/${pbx.context}/settings/webhooks/${webhookEvent.webhookId}`}
              >
                Webhook
              </Button>
            }
          />
        </HeaderCard>
        <Stack
          sx={{
            gap: 2,
            mt: 2,
          }}
        >
          <FormGroup title="About this event">
            <Grid size={{ xs: 6, lg: 4 }}>
              <StringForm
                label="Webhook"
                text={
                  <DataCell
                    startDecorator={<Avatar text={webhookEvent.name} px={24} icon={viWebhooks} />}
                    primary={webhookEvent.name}
                  />
                }
              />
            </Grid>
            <Grid size={{ xs: 6, lg: 4 }}>
              <StringForm label="Date" text={format(webhookEvent.date, "d MMMM yyyy, HH:mm:ss")} />
            </Grid>
            <Grid size={{ xs: 6, lg: 4 }}>
              <StringForm label="Event type" text={webhookEvent.eventType} />
            </Grid>
            <Grid size={{ xs: 6, lg: 4 }}>
              <StringForm
                label="DID"
                text={
                  webhookEvent.did ?? (
                    <Typography variant="body2">
                      <i style={{ opacity: "0.6" }}>None</i>
                    </Typography>
                  )
                }
              />
            </Grid>
            <Grid size={{ xs: 6, lg: 4 }}>
              <StringForm
                label="Response status code"
                text={
                  webhookEvent.responseStatusCode ?? (
                    <Typography variant="body2">
                      <i style={{ opacity: "0.6" }}>None</i>
                    </Typography>
                  )
                }
              />
            </Grid>
            <Grid size={12}>
              <StringForm
                label="URL"
                text={
                  <Typography variant="body2" sx={{ wordWrap: "break-word" }}>
                    {webhookEvent.url}
                  </Typography>
                }
              />
            </Grid>
          </FormGroup>
          {isDetailOpen && webhookEvent.responseData ? (
            <ResponseDataCard responseData={webhookEvent.responseData} />
          ) : null}
          <Stack
            direction="row"
            sx={{
              justifyContent: "flex-end",
              gap: 1,
              containerType: "inline-size",
            }}
          >
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
}

function ResponseDataCard({ responseData }: { responseData?: string }) {
  const content = useMemo(() => {
    if (responseData) {
      try {
        const parsedJson = JSON.parse(responseData);
        return JSON.stringify(parsedJson, null, 2);
      } catch {
        return responseData;
      }
    }

    return responseData;
  }, [responseData]);

  const codeBlockProps = useMemo(
    () =>
      createHighlightedCodeBlockProps({
        title: "Response data",
        highlighter,
        code: content || "",
        lang: "json",
      }),
    [content],
  );

  return <CodeBlock {...codeBlockProps} />;
}
